import {
  fetchCostRates,
  fetchCostsByEnvironments,
  fetchEnvironments,
} from "@app/deploy";
import {
  type DeployEnvironmentRow,
  selectEnvironmentsByStackIdForTable,
  selectEnvironmentsForTable,
} from "@app/environment-table";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useCompositeLoader, useSelector } from "@app/react";
import {
  createEnvUrl,
  environmentAppsUrl,
  environmentDatabasesUrl,
} from "@app/routes";
import type { DeployEnvironment } from "@app/types";
import { Link, useNavigate } from "react-router-dom";
import { ButtonAnyOwner } from "../button";
import { costColDef } from "../cost-estimate-tooltip";
import { DataTable, idColDef } from "../datatable";
import {
  CurrencyCell,
  DateTimeCell,
  EnvironmentCell,
} from "../datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "../datatable/datatable";
import { Group } from "../group";
import { IconPlusCircle } from "../icons";
import { TitleBar } from "../resource-list-view";
import { tokens } from "../tokens";

export const EnvironmentItemView = ({ env }: { env: DeployEnvironment }) => {
  return (
    <Link to={environmentAppsUrl(env.id)} className="flex">
      <img
        src="/resource-types/logo-environment.png"
        className="w-[32px] h-[32px] mr-2 align-middle"
        aria-label="Environment"
      />
      <p className={`${tokens.type["table link"]} leading-8`}>{env.handle}</p>
    </Link>
  );
};

// ─── Shared column definitions ───────────────────────────────────────────────

const colDefs: Record<string, DataTableColDef<DeployEnvironmentRow>> = {
  handle: {
    field: "handle",
    headerName: "Environment",
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployEnvironmentRow>) => (
      <EnvironmentCell id={data.id} />
    ),
  },
  id: {
    field: "id",
    ...idColDef,
    valueGetter: ({ data }) => Number.parseInt(data?.id || "0", 10),
  },
  totalAppCount: {
    field: "totalAppCount",
    headerName: "Apps",
    initialWidth: 100,
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployEnvironmentRow>) => (
      <Link
        to={environmentAppsUrl(data.id)}
        className={tokens.type["table link"]}
      >
        {data.totalAppCount}
      </Link>
    ),
  },
  totalDatabaseCount: {
    field: "totalDatabaseCount",
    headerName: "Databases",
    initialWidth: 100,
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployEnvironmentRow>) => (
      <Link
        to={environmentDatabasesUrl(data.id)}
        className={tokens.type["table link"]}
      >
        {data.totalDatabaseCount}
      </Link>
    ),
  },
  createdAt: {
    field: "createdAt",
    headerName: "Created At (UTC)",
    initialHide: true,
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployEnvironmentRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
  cost: {
    field: "cost",
    ...costColDef,
    sort: "desc",
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployEnvironmentRow>) => (
      <CurrencyCell value={data.cost} />
    ),
  },
};

const environmentListColDefs = [
  colDefs.handle,
  colDefs.id,
  colDefs.totalAppCount,
  colDefs.totalDatabaseCount,
  colDefs.createdAt,
  colDefs.cost,
];

export function EnvironmentListByOrg() {
  const orgId = useSelector(selectOrganizationSelectedId);
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchEnvironments,
    fetchCostRates({ orgId }),
    fetchCostsByEnvironments({ orgId }),
  ]);
  const navigate = useNavigate();
  const onCreate = () => navigate(createEnvUrl(""));
  const envs = useSelector(selectEnvironmentsForTable);

  return (
    <Group size="xl">
      <Group variant="horizontal" className="items-center justify-between">
        <TitleBar description="Environments in Stacks group resources and share networks with others on the same Stack.">
          Environments
        </TitleBar>
        <ButtonAnyOwner onClick={onCreate} tooltipProps={{ placement: "left" }}>
          <IconPlusCircle variant="sm" className="mr-2" />
          New Environment
        </ButtonAnyOwner>
      </Group>

      <DataTable<DeployEnvironmentRow>
        rowData={envs}
        colDefs={environmentListColDefs}
        isLoading={!envs.length && (isLoading || isInitialLoading)}
        syncQueryParams={true}
      />
    </Group>
  );
}

const StackEmptyEnvironments = () => (
  <div className="border border-gray-200 rounded-lg bg-white p-10 text-center">
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      No Environments
    </h3>
    <p className="text-gray-600 max-w-lg mx-auto">
      This stack has no environments or running resources. Create an environment
      and provision an app or database to get started.{" "}
      <a
        href="https://www.aptible.com/docs/core-concepts/architecture/environments#environments"
        target="_blank"
        rel="noreferrer"
        className="text-indigo-600 hover:underline"
      >
        Learn more about Environments
      </a>
    </p>
  </div>
);

export function EnvironmentListByStack({ stackId }: { stackId: string }) {
  const orgId = useSelector(selectOrganizationSelectedId);
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchEnvironments,
    fetchCostRates({ orgId }),
    fetchCostsByEnvironments({ orgId }),
  ]);
  const navigate = useNavigate();
  const onCreate = () => navigate(createEnvUrl(`stack_id=${stackId}`));
  const envs = useSelector((s) =>
    selectEnvironmentsByStackIdForTable(s, { stackId }),
  );

  const isEmpty = !isLoading && !isInitialLoading && envs.length === 0;

  return (
    <Group size="lg">
      <div className="flex justify-end">
        <ButtonAnyOwner onClick={onCreate} tooltipProps={{ placement: "left" }}>
          <IconPlusCircle variant="sm" className="mr-2" />
          New Environment
        </ButtonAnyOwner>
      </div>

      {isEmpty ? (
        <StackEmptyEnvironments />
      ) : (
        <DataTable<DeployEnvironmentRow>
          rowData={envs}
          colDefs={environmentListColDefs}
          isLoading={!envs.length && (isLoading || isInitialLoading)}
          syncQueryParams={true}
        />
      )}
    </Group>
  );
}
